export type MarketingMode = 'OptIn' | 'OptOut';

/** Settings returned by GET /api/shop/marketing-settings (from the server's Marketing app settings). */
export interface MarketingSettings {
    enabled: boolean;
    mode: string;
    wordingVersion: string;
    privacyPolicyUrl: string;
}

export interface MarketingWording {
    /** Mode this wording was written for; the configured mode must match it. */
    mode: MarketingMode;
    intro?: string;
    checkboxLabel: string;
    /** Text shown before the privacy policy sentence. */
    footnote?: string;
}

/** Marketing fields sent with the order. All absent when the marketing choice is not shown. */
export interface MarketingOrderFields {
    marketingMode?: MarketingMode;
    marketingCheckboxTicked?: boolean;
    marketingWordingVersion?: string;
}

/**
 * Wording shown to the customer, by version. The version is stored with the order,
 * so change wording by adding a new version rather than editing an existing one.
 */
export const MARKETING_WORDING: Readonly<Record<string, MarketingWording>> = {
    A1: {
        mode: 'OptIn',
        checkboxLabel:
            'Text me offers and news from The Ice Cream Farm. You can opt out at any time by replying STOP.',
    },
    B1: {
        mode: 'OptOut',
        intro: "We'll occasionally text you offers and news from The Ice Cream Farm.",
        checkboxLabel: "Tick here if you'd prefer not to receive these",
        footnote: 'You can opt out at any time by replying STOP.',
    },
};

const SAFE_LINK_PATTERN = /^https?:\/\//i;

/**
 * Returns the wording to show, or null when the marketing choice should not be shown:
 * the feature is switched off, the settings have not loaded, the version is unknown,
 * or the configured mode does not match the version's mode.
 */
export const resolveMarketingWording = (
    settings: MarketingSettings | null | undefined
): MarketingWording | null => {
    if (!settings?.enabled) {
        return null;
    }

    if (!Object.prototype.hasOwnProperty.call(MARKETING_WORDING, settings.wordingVersion)) {
        return null;
    }

    const wording = MARKETING_WORDING[settings.wordingVersion];
    return wording.mode === settings.mode ? wording : null;
};

/** Returns the privacy policy URL when it is an http(s) link, otherwise null. */
export const getPrivacyPolicyLink = (url: string | null | undefined): string | null => {
    const trimmed = url?.trim() ?? '';
    return SAFE_LINK_PATTERN.test(trimmed) ? trimmed : null;
};

/** Builds the marketing fields for the order. Returns no fields when the marketing choice is not shown. */
export const buildMarketingOrderFields = (
    settings: MarketingSettings | null | undefined,
    checkboxTicked: boolean
): MarketingOrderFields => {
    const wording = resolveMarketingWording(settings);

    if (!wording) {
        return {};
    }

    return {
        marketingMode: wording.mode,
        marketingCheckboxTicked: checkboxTicked,
        marketingWordingVersion: settings.wordingVersion,
    };
};
